import { Color, Spacing } from '@persona-id/design-tokens';
import { noop } from 'lodash';
import { FC, Fragment, ReactNode, useMemo } from 'react';
import { BORDER_RADIUSES, COLORS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import ConditionalConfiguration from 'dashboard/apps/InquiryTemplateEditor/WorkflowStepPanel/ConditionalConfiguration';
import {
  createInputStep,
  generateJsonSchemaConfig,
  generateUsableObjects,
} from 'dashboard/lib/workflows';
import { TriggerDirect } from 'dashboard/models/trigger';
import { WorkflowSchema } from 'dashboard/models/workflow';
import {
  WorkflowConditional,
  WorkflowStep,
  WorkflowStepConditional,
} from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import { buildGraph, findPath } from 'lib/workflows';

const RouteList = styled.div`
  background-color: ${COLORS.white};
  transition:
    background-color 0.4s ease-in-out,
    box-shadow 0.4s ease-in-out;
`;

const ItemContainer = styled.section`
  padding: ${Spacing[2]};
  flex: 1;
`;

const ElseContainer = styled.div<{
  inRunPath: boolean;
}>`
  ${({ inRunPath }) =>
    inRunPath &&
    css`
      border: ${Color.CoreBlue700} solid 2px;
    `};
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[2]};
  display: flex;
  flex-direction: column;
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: 15% 85%;
`;

const StyledParagraph = styled(Paragraph)`
  padding-top: 13px;
  padding-bottom: ${Spacing[2]};
  padding-left: ${Spacing[1]};
  font-size: 13px;
  line-height: ${Spacing[4]};
`;

const RouteName = styled(Label).attrs(() => ({ size: 'lg', weight: 'bold' }))`
  display: -webkit-box;
  padding-right: ${Spacing[1]};
  overflow: hidden;
  white-space: normal;
  word-wrap: pre-wrap;
  text-overflow: ellipsis;
  margin-right: 0;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

interface WorkflowConfigurationConditionalProps {
  trigger: TriggerDirect;
  step: WorkflowStepConditional;
  stepsConfig: WorkflowStep[];
  workflowSchema: WorkflowSchema;
  getInInquiryInspectRunPath: (routeNumber: number) => boolean;
}

const WorkflowConditionalPreview: FC<WorkflowConfigurationConditionalProps> = ({
  trigger,
  step,
  stepsConfig,
  workflowSchema,
  getInInquiryInspectRunPath,
}) => {
  const { conditionals } = step;

  const tokensToSteps = useMemo(() => buildGraph(stepsConfig), [stepsConfig]);
  const stepsInPath = useMemo(() => findPath(step.token, stepsConfig), [step.token, stepsConfig]);
  const criteriaVariableSchemas = workflowSchema['criteria-variable-schemas'];
  const usableObjects = useMemo(() => {
    return generateUsableObjects({
      inputStep: createInputStep({ trigger }),
      stepsToProcess: stepsInPath,
      activeStepToken: step.token,
      criteriaVariableSchemas: criteriaVariableSchemas,
    });
  }, [trigger, stepsInPath, step.token, criteriaVariableSchemas]);

  const { jsonSchema, tokensToDisplayValue } = useMemo(
    () =>
      generateJsonSchemaConfig({
        usableObjects: usableObjects,
      }),
    [usableObjects],
  );

  const conditionalSections: ReactNode[] = useMemo(
    () => [
      ...conditionals.map((conditional, index) => {
        if (index === conditionals.length - 1) {
          return null;
        }
        return (
          <Fragment key={conditional.token}>
            <ItemContainer>
              <ConditionalConfiguration
                editable={false}
                deleteEnabled={false}
                isLimited={false}
                hideContext={true}
                errors={[]}
                conditional={conditional as WorkflowConditional}
                usableObjects={usableObjects}
                jsonSchema={jsonSchema}
                tokensToDisplayValue={tokensToDisplayValue}
                tokensToSteps={tokensToSteps}
                routeIndex={index}
                deleteRoute={noop}
                setConditionalRouteToEdit={noop}
                inInquiryInspectRunPath={getInInquiryInspectRunPath(index)}
              />
            </ItemContainer>
          </Fragment>
        );
      }),
      <Fragment key='else'>
        <ItemContainer>
          <RouteName>Failed</RouteName>
          <Spacer height={1} />
          <ElseContainer inRunPath={getInInquiryInspectRunPath(conditionals.length - 1)}>
            <GridRow>
              <StyledParagraph size='sm'>Else</StyledParagraph>
              <div />
            </GridRow>
          </ElseContainer>
        </ItemContainer>
      </Fragment>,
    ],
    [conditionals, usableObjects, jsonSchema, tokensToDisplayValue, tokensToSteps],
  );

  return (
    <>
      <RouteList>{conditionalSections}</RouteList>
    </>
  );
};

export default WorkflowConditionalPreview;
